<template>
  <q-input
    v-model="searchVal"
    hide-bottom-space
    stack-label
    clearable
    :class="inputClass"
    :outlined="outlined"
    :dense="dense"
    :error="error"
    :error-message="errorMessage"
    :loading="loading"
    :borderless="borderless"
    :label="label"
    :placeholder="placeholder"
    :autofocus="autofocus"
    :debounce="debounce"
    @update:model-value="performSearch"
  >
    <template #prepend>
      <slot name="prepend">
        <q-icon v-if="icon" :name="icon" />
      </slot>
    </template>
  </q-input>

  <q-list padding :separator="separator">
    <slot v-if="hasCreateSlot" name="create" />
    <slot v-if="hasAdvancedSlot" name="advanced" />

    <template v-if="results.length > 0">
      <template v-for="(item, index) in results">
        <slot
          name="selected-item"
          v-bind="{ item, index, selected: isResultSelected(item.id) }"
        />
      </template>
    </template>

    <no-results
      v-if="results.length < 1"
      without-icon
      title="No results to display"
      align="left"
    />
  </q-list>
</template>

<script>
import input from 'src/helpers/input'

/**
 * Base implementation for any modules which require an input field for model searching
 * Displays a search box and any items underneath it
 *
 * SLOTS:
 * - prepend: prepend content to left of input
 * - create: slot above items to show a + button
 * - advanced: slot above items (under create) to show advanced filtering items
 * - selected-item: (item, index, selected) - render each individual item being searched
 *
 * EMIT:
 * - clear - model state is cleared
 * - input: (val) model state change
 * - clickedResult: (val) result which was clicked / added to the model
 * - removedResult: (val) result which was clicked (deselected) and removed from the model
 */
export default {
  mixins: [input],
  props: {
    borderless: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String
    },
    label: {
      type: String
    },
    placeholder: {
      type: String
    },
    multiple: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    separator: {
      type: Boolean,
      default: false
    },
    // How long after typing in the text box should it search
    debounce: {
      type: Number,
      default: 750
    },
    // Icon to display to left
    icon: {
      type: String
    },
    inputClass: {
      type: [String, Object]
    },
    modelValue: {
      type: [Array, Object],
      default: () => {
        return []
      }
    },
    // Function for performing a search
    // Param: val (the value to search over)
    search: {
      type: Function,
      required: true
    },
    // Options to display in list
    results: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      searchVal: ''
    }
  },
  computed: {
    hasCreateSlot () {
      return !!this.$slots.create
    },
    hasAdvancedSlot () {
      return !!this.$slots.advanced
    }
  },
  methods: {
    performSearch () {
      return this.search(this.searchVal)
    }
  },
  mounted () {
    this.performSearch()
  }
}
</script>
